import { effect, inject, Inject, Injectable, signal, WritableSignal } from '@angular/core';
import { PaymentIntent, PaymentIntentResult } from "@stripe/stripe-js";
import { IClientAuthorizeCallbackData, IOnApproveCallbackData } from "ngx-paypal";
import { map, Observable } from "rxjs";
import { EmailValidator } from "../form/validator/email-validator";
import { Customer, NewCustomerResponseInterface, PostalAddressInterface } from "../models/customer";
import {
  CheckoutInterface,
  CouponInterface,
  CouponJsonInterface,
  DiscountInterface,
  OnlineOrderProcessInterface,
  OrderAgreementsInterface,
  OrderBasicInterface,
  OrderInterface,
  OrderJsonInterface,
  OrderPersistedInterface,
  OrderProcessInterface,
  OrderResponseInterface,
  OrderSubmitInterface,
  RefundableOrderInterface,
  RefundableOrderResponseInterface,
  RefundableOrderViewInterface,
  RefundRequestInterface,
  RefundSubmitInterface,
  TaxesByRateInterface,
  TrackingLinkInterface,
} from "../models/order";
import { SelectedBuildupServiceInterface } from "../product/buildup-service/buildup-service.component";
import { CartService } from "./cart.service";
import { RefundCartItem } from "../models/cart";
import { HttpOrderService } from "./http/order.service";
import {
  EnvService,
  LocalstorageService,
  PageLoadService,
  PaymentMethodInterface
} from "@gea-waldviertler-website/shop-lib";
import { CustomerService } from "./customer.service";
import { positiveOrZero } from "../helpers";
import { IdValueInterface, ShippingMethodJsonInterface } from "@gea-waldviertler-website/gea-models";
import * as Sentry from "@sentry/angular";
import { ValidationErrors } from "@angular/forms";
import { ToastService } from "./toast.service";

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  /**
   * this is the response after submitting the order
   *
   * @private
   */
  private orderResponse?: OrderResponseInterface

  /**
   * this holds the arbitrary order object and will be changed by different
   * methods. to avoid unnecessary signal updates in each method, changes
   * are broadcasted in the persist method only
   *
   * @private
   */
  private _order: OrderInterface

  /**
   * this signal is used to broadcast changes and can be fetched by
   * order getter method readonly and must be updated in onChange() only
   * 
   * @private
   */
  private orderSignal: WritableSignal<OrderInterface>

  /**
   * this signal is used to broadcast changes and can be fetched by
   * status getter method readonly and must be updated in validate() only
   *
   * @private
   */
  private statusSignal: WritableSignal<CheckoutInterface>

  public formErrorReceiver = signal<IdValueInterface<ValidationErrors>>({})

  /**
   * this is used to check user login and logout on customer signal change
   *
   * @private
   */
  private customerId: number = 0

  private envService = inject(EnvService)

  constructor(
    @Inject(CustomerService) private customerService: CustomerService,
    @Inject(LocalstorageService) private storage: LocalstorageService,
    @Inject(CartService) private cartService: CartService,
    @Inject(HttpOrderService) private httpService: HttpOrderService,
    @Inject(PageLoadService) private pageLoadService: PageLoadService,
    @Inject(ToastService) private toastService: ToastService,
  ) {

    // fetch from localstorage or create an empty order
    const o = this.getFromResponse(this.storage.get('order') as OrderResponseInterface)
    if (o.addressShipping?.country) {
      this.cartService.setTaxCountry(o.addressShipping?.country)
    }

    // reset payment method to prevent errors
    o.paymentMethod = undefined

    this._order = {
      ... o,
      ... this.calculateTaxAmounts(o) // calculate tax amounts
    }

    // instantiate order signal
    this.orderSignal = signal(this._order)

    // instantiate checkout status signal
    this.statusSignal = signal(this.getInitialStatus())

    this.cartService.onRemove.subscribe(item => {
      this.httpValidation()
    })

    // close payment accordion item on form errors
    // currently only the address forms are handled
    effect(() => {
      const errors = this.formErrorReceiver()
      let e = false

      if (errors["shipping"] && ! this.validateShippingAddress()) e = true
      if (errors["billing"] && ! this.validateBillingAddress()) e = true

      if (e && this.order().paymentMethod) {
        this.setPaymentMethod(undefined)
      }
    })

    effect(() => {
      const cart = this.cartService.cart()
      if (cart && cart.items.length) this.onChange(true)
    })

    effect(() => {
      const customer = this.customerService.customer()

      // no customer was logged in before
      if (this.customerId === 0) {
        // no customer, do nothing
        if (! customer) return

        this.onCustomerLogin(customer)

      // customer was logged in before
      } else {
        // no customer, call on logout
        if (! customer) {
          this.onCustomerLogout()
        }
      }

      if (customer) this.customerId = customer.id
      else this.customerId = 0
    })
  }

  private onCustomerLogout(): void {
    // remove addresses from checkout on logout
    this.setBillingAddress(undefined)
    this.setShippingAddress(undefined)
    this.customerId = 0
  }

  private onCustomerLogin(customer: Customer): void {
    let o = this.orderSignal()

    // set addresses from customer on login if no address is set in checkout already
    if (! o.billingSameAsShippingAddress || ! this.status().needed.addressShipping) {
      const ba = customer.getBillingAddress()
      if (ba) this.setBillingAddress(ba, false)
    }

    const sa = customer.getShippingAddress()
    if (sa) this.setShippingAddress(sa, true)

    this.customerId = customer.id
    this.validate()
  }

  public get order() {
    return this.orderSignal.asReadonly()
  }

  public get status() {
    return this.statusSignal.asReadonly()
  }

  private getInitialStatus(): CheckoutInterface {
    return {
      invalid: false,
      touched: false,
      httpInvalid: false,
      validation: {},
      needed: {}
    }
  }

  /**
   * This mutates a RefundableOrderResponseInterface to a RefundableOrderInterface
   *
   * @param o
   */
  public getFromResponse(o: RefundableOrderResponseInterface): RefundableOrderInterface
  /**
   * This mutates a OrderResponseInterface to a OrderInterface
   *
   * @param o
   */
  public getFromResponse(o?: OrderResponseInterface): OrderInterface
  public getFromResponse(o?: OrderResponseInterface | RefundableOrderResponseInterface): OrderInterface | RefundableOrderInterface {
    if (! o) return this.getNewOrder()

    const bsas =
      (o.addressBilling === undefined && o.addressShipping === undefined) // OR
      || (( o.addressBilling !== null || o.addressShipping !== null) && // XOR
        (o.addressBilling === null || o.addressShipping === null))
      || this.isAddressEqual(o.addressBilling, o.addressShipping, true) // OR

    const base = {
      ... o,
      ... this.calculateTaxAmounts(o),
      billingSameAsShippingAddress: bsas,
    }

    if (this.isRefundableOrderResponse(o)) {
      return {
        ... base,
        cart: {
          uuid: o.cart.uuid,
          items: o.cart.items.map(item => new RefundCartItem(item))
        }
      } as RefundableOrderInterface
    }

    return base as OrderInterface
  }

  private isRefundableOrderResponse(o: OrderResponseInterface | RefundableOrderResponseInterface): o is RefundableOrderResponseInterface {
    return (o as RefundableOrderResponseInterface).uuid !== undefined
  }

  private getNewOrder(): OrderInterface {
    return {
      addressShipping: undefined,
      addressBilling: undefined,
      paymentMethod: undefined,
      shippingMethod: undefined,
      amountItems: 0,
      amountDiscount: 0,
      amountShipping: 0,
      amountTax: 0,
      amountCoupons: 0,
      amountInvoice: 0,
      amountPayment: 0,
      agreementsChecked: false,
      coupons: [],
      discount: undefined,
      subscriptions: this.envService.config.subscribeItems,
      message: '',
      billingSameAsShippingAddress: true,
      taxesByRate: []
    }
  }

  public setBuildupService(service?: SelectedBuildupServiceInterface) {
    if (! service) {
      this.cartService.disableBuildUpService()
    }
    
    this._order.buildupService = service
    this.onChange()
  }

  public setShippingAddress(address: PostalAddressInterface | undefined, persist: boolean = true) {
    if (this.isAddressEqual(this._order.addressShipping, address)) return
    // this.updateCustomersAdresses(address)
    // this.prepareCustomerName()
    this._order.addressShipping = address
    this.onChange(persist)
  }

  public setBillingAddress(address: PostalAddressInterface | undefined, persist: boolean = true) {
    if (this.isAddressEqual(this._order.addressBilling, address)) return
    // this.updateCustomersAdresses(address)
    // this.prepareCustomerName()
    this._order.addressBilling = address
    this.onChange(persist)
  }

  public setBillingSameAsShippingAddress(v: boolean) {
    if (this._order.billingSameAsShippingAddress === v) return
    this._order.billingSameAsShippingAddress = v
    this._order.addressBilling = undefined
    this.onChange()
  }

  public setPaymentClientSecret(secret: string) {
    this.storage.set('payment_client_secret', secret)
  }

  public getPaymentClientSecret(): string | null {
    return this.storage.get('payment_client_secret')
  }

  clearPaymentClientSecret() {
    this.storage.remove('payment_client_secret')
  }

  setAgreements(agreements: OrderAgreementsInterface) {
    this._order.agreementsChecked = agreements.agreementsChecked
    this._order.subscriptions = agreements.subscriptions
    this._order.message = agreements.message
    this.onChange()
  }

  public setEmail(email: string | undefined) {
    if (this._order.email === email) return
    this._order.email = email
    this.onChange()
  }

  setNewPassword(password?: string) {
    if (this._order.password === password) return
    this._order.password = password
    this.onChange()
  }

  setPaymentMethod(method: PaymentMethodInterface | undefined) {
    if (this._order.paymentMethod === method) return
    this._order.paymentMethod = method
    this.onChange()
  }

  setPaymentIntent(paymentIntent: PaymentIntent) {
    if (this._order.paymentMethod) {
      this._order.paymentMethod.paymentIntent = paymentIntent
    }
  }

  setAuthorizationData(authData: IOnApproveCallbackData | PaymentIntentResult) {
    if (this._order.paymentMethod) {
      this._order.paymentMethod.authorizationData = authData
    }
  }

  setCoupons(coupons: CouponInterface[]) {
    if (JSON.stringify(this._order.coupons) === JSON.stringify(coupons)) return
    this._order.coupons = coupons
    this.onChange()
  }

  /**
   * Here the Shipping Method will be chosen by api request and after that onAfterChange will be called
   *
   * @param persist
   */
  onChange(persist: boolean = true): void {
    const c = this.cartService.cart()

    // check shipping method by cart items if set
    if (c.items.length && this._order.addressShipping) {
      this.httpService.getShippingMethod(c.items, this._order.addressShipping).subscribe(response => {
        if (response.success) {
          this._order.shippingMethod = response.shipping
          this._order.amountShipping = response.shipping.price
          this.onAfterChange(persist)
        }
      }, error => {
        this._order.shippingMethod = undefined
        this._order.amountShipping = 0
        this.onAfterChange(persist)
      })
    } else {
      this.onAfterChange(persist)
    }
  }

  public calculateTaxAmounts(order: OrderBasicInterface): Partial<OrderInterface | OrderResponseInterface> {
    // set tax by country and split taxes by rate for nice output
    if (order.addressShipping?.country) {
      return {
        amountTax: Math.round(this.cartService.getTaxAmount(order.addressShipping?.country) * 100) / 100,
        taxesByRate: this.cartService.getTaxesByRate(order.addressShipping?.country, this.getShippingAmount())
      }
    }

    return {
      amountTax: 0,
      taxesByRate: []
    } as Partial<OrderInterface | OrderResponseInterface>
  }

  onAfterChange(persist: boolean = true) {

    // store pickup does not need a shipping address, but a billing address
    if (! this.checkNeedsShippingAddress()) {
      if ((! this._order.addressBilling) && this._order.addressShipping) {
        this._order.addressBilling = this._order.addressShipping
        this._order.addressShipping = undefined
      } else if (! this._order.addressBilling) {
        const c = this.customerService.customer()
        if (c) {
          this._order.addressBilling = c.getBillingAddress()
        }
      }
    }

    if (this._order.addressShipping?.country) {
      this.cartService.setTaxCountry(this._order.addressShipping?.country)
    }

    let cartAmounts = this.cartService.amounts()

    // tax shipping is calculated using the highest tax rate of the items
    const taxShipping = (cartAmounts.highestTaxRate ?
      (this._order.amountShipping / (100 + cartAmounts.highestTaxRate) * cartAmounts.highestTaxRate)
      : 0)

    const tax = cartAmounts.totalTax + taxShipping
    let taxesByRate = cartAmounts.taxesByRate ? JSON.parse(JSON.stringify(cartAmounts.taxesByRate)) as TaxesByRateInterface[] : []

    if (taxShipping) {
      taxesByRate = taxesByRate.map(v => {
        if (v.rate == cartAmounts.highestTaxRate) {
          v.amount += taxShipping
        }
        return v
      })
    }

    this._order = {
      ... this._order,
      amountTax: tax,
      taxesByRate: taxesByRate
    }

    // set amounts
    this._order.amountItems = cartAmounts.total
    this._order.amountDiscount = this.cartService.getItemDiscount()
    this._order.amountInvoice =  cartAmounts.totalOnlineOrder - this._order.amountDiscount + this._order.amountShipping

    // handle coupons
    this._order.amountCoupons = 0
    if (this._order.coupons) {
      for (let coupon of this._order.coupons) {
        if (coupon.type == "PERCENT") continue
        this._order.amountCoupons += coupon.value
      }
    }
    if (this._order.amountCoupons > this._order.amountInvoice) {
      this._order.amountCoupons = this._order.amountInvoice
    }

    // set payment amount = invoice amount - coupons amount
    this._order.amountPayment = this._order.amountInvoice - this._order.amountCoupons
    
    // persist order in localstorage and broadcast change
    if (persist) this.persist()
    
    // validate and broadcast validity info
    this.validate()
  }

  getBuildupService(postalcode: string) {
    return this.httpService.getBuildupService(postalcode)
  }

  setOrderResponse(response?: OrderResponseInterface) {
    this.orderResponse = response
    if (response) {
      this.storage.set('orderResponse', response)
    } else {
      this.storage.remove('orderResponse')
    }
  }
  
  getOrderResponse(): OrderResponseInterface | undefined {
    if (this.orderResponse) return this.orderResponse
    this.orderResponse = this.storage.get('orderResponse')
    return this.orderResponse
  }

  getShippingAmount(): number {
    if (this._order?.shippingMethod) {
      return this._order.shippingMethod?.price
    }
    return 0
  }

  /**
   * maybe this can be replaced by _order.amountPayment, calculated in onAfterChange - use
   * positiveOrZero Pipe to display or positiveOrZero function to mutate
   *
   * @deprecated
   */
  getPaymentAmount() {
    return positiveOrZero(this.order().amountInvoice - this.order().amountCoupons)
  }

  applyDiscount(discount?: DiscountInterface): void {
    this._order.discount = discount

    // remove discount if undefined
    if (! discount) {
      this._order.amountDiscount = 0
      this.persist()
      this.cartService.applyDiscount(discount)

      return
    }

    // copy order and prepare coupons
    this.httpService.checkDiscount(this.prepareOrderForSubmit()).subscribe(checkedOrder => {
      if (checkedOrder.validationMessages && checkedOrder.validationMessages.length) {
        this.parseAndReplaceDirtyOrder(checkedOrder)
      }
    })
  }

  httpValidation() {
    this.httpService.validate(this.prepareOrderForSubmit()).subscribe(validatedOrder => {
      this.parseAndReplaceDirtyOrder(validatedOrder)
    }, error => {
      Sentry.captureException(error)
      this.toastService.showError('Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.')
    })
  }

  validate(touched?: boolean): boolean {
    let statusBefore = this.status()
    let changed = false

    let statusAfter = this.getInitialStatus()

    // check touched changed
    if (touched !== undefined && statusBefore.touched !== touched) {
      changed = true
      statusAfter.touched = touched
    }

    // check needs before validating
    if (this.checkNeedsPhone()) {
      statusAfter.needed.phone = { message: null }
      if (! statusBefore.needed.phone) changed = true
    } else {
      if (statusBefore.needed.phone) changed = true
    }

    if (this.checkNeedsShippingAddress()) {
      statusAfter.needed.addressShipping = { message: null }
      if (! statusBefore.needed.addressShipping) changed = true
    } else {
      if (statusBefore.needed.addressShipping) changed = true
    }

    // start validating
    let valid = true

    if (! this.validateAgreementsChecked()) {
      statusAfter.validation.agreements = { message: null }
      if (! statusBefore.validation.agreements) changed = true
      valid = false
    } else {
      if (statusBefore.validation.agreements) changed = true
    }

    if (! this.validateEmail()) {
      statusAfter.validation.email = { message: null }
      if (! statusBefore.validation.email) changed = true
      valid = false
    } else {
      if (statusBefore.validation.email) changed = true
    }

    if (! this.validateAmounts()) {
      statusAfter.validation.amounts = { message: null }
      if (! statusBefore.validation.amounts) changed = true
      valid = false
    } else {
      if (statusBefore.validation.amounts) changed = true
    }

    // validate shipping address if needed
    if (statusAfter.needed.addressShipping) {
      if (! this.validateShippingAddress()) {
        statusAfter.validation.addressShipping = { message: null }
        if (! statusBefore.validation.addressShipping) changed = true
        valid = false
      } else {
        if (statusBefore.validation.addressShipping) changed = true
      }
    } else {
      if (statusBefore.validation.addressShipping) changed = true
    }

    if (! this.validateBillingAddress()) {
      statusAfter.validation.addressBilling = { message: null }
      if (! statusBefore.validation.addressBilling) changed = true
      valid = false
    } else {
      if (statusBefore.validation.addressBilling) changed = true
    }

    if (! this.validateCartItems()) {
      statusAfter.validation.cartItems = { message: null }
      if (! statusBefore.validation.cartItems) changed = true
      valid = false
    } else {
      if (statusBefore.validation.cartItems) changed = true
    }

    // validate shipping method if needed
    if (statusAfter.needed.addressShipping) {
      if (! this.validateShippingMethod()) {
        statusAfter.validation.shippingMethod = { message: null }
        if (! statusBefore.validation.shippingMethod) changed = true
        valid = false
      } else {
        if (statusBefore.validation.shippingMethod) changed = true
      }
    } else {
      if (statusBefore.validation.shippingMethod) changed = true
    }

    // validate phone number if needed
    if (statusAfter.needed.phone) {
      if (statusAfter.needed.addressShipping) {
        if (! this.validatePhoneShipping()) {
          statusAfter.validation.phone = { message: null }
          if (! statusBefore.validation.phone) changed = true
          valid = false
        } else {
          if (statusBefore.validation.phone) changed = true
        }
      } else {
        if (! this.validatePhoneBilling()) {
          statusAfter.validation.phone = { message: null }
          if (! statusBefore.validation.phone) changed = true
          valid = false
        } else {
          if (statusBefore.validation.phone) changed = true
        }
      }
    } else {
      if (statusBefore.validation.phone) changed = true
    }

    statusAfter.httpInvalid = statusBefore.httpInvalid

    if (changed) this.statusSignal.set(statusAfter)

    if (statusAfter.invalid && this._order.paymentMethod !== undefined) {
      this.setPaymentMethod(undefined)
    }

    return valid
  }

  checkNeedsPhone(): boolean {
    const c = this.cartService.cart()
    // check if telephone number is needed
    for (const item of c.items) {
      if (item.distinctProduct.product?.isFurniture()) return true
      if (item.store) return true
    }

    return false
  }

  checkNeedsShippingAddress(): boolean {
    const c = this.cartService.cart()
    for (const i of c.items) {
      if (! i.store) return true
    }

    return false
  }

  validatePhoneShipping(): boolean {
    // look for phone number in shipping address
    let phoneProvided = false
    if (this.order().addressShipping
      && (this.order().addressShipping?.telephone + '').length > 7) {
      phoneProvided = true
    }

    return phoneProvided
  }

  validatePhoneBilling(): boolean {
    // look for phone number in billing address
    let phoneProvided = false
    if (this.order().addressBilling
      && (this.order().addressBilling?.telephone + '').length > 7) {
      phoneProvided = true
    }

    return phoneProvided
  }

  validateCartItems(): boolean {
    const c = this.cartService.cart()
    return c.items && c.items.length > 0
  }

  validateAgreementsChecked(): boolean {
    return this.orderSignal().agreementsChecked
  }

  /**
   * look for items to buy in online store (store property set to undefined)
   * 
   * @private
   */
  private needsShippingAddress() {
    const c = this.cartService.cart()
    for (const i of c.items) {
      if (! i.store) return true
    }
    return false
  }
  
  validateShippingAddress(): boolean {
    const o = this.orderSignal()
    
    // no shipping method given?
    if (! o.shippingMethod) return false
    const cu = this.customerService.customer()
    if (cu) {
      return this.validateAddressParts(o.addressShipping)
    }
    const valid = ! this.formErrorReceiver()["shipping"]
    return valid
  }


  validateBillingAddress(): boolean {
    const o = this.orderSignal()
    // check billingSameAsShipping only if shipping address is needed
    // otherwise the billing address is mandatory
    if (this.needsShippingAddress()) {
      if (o.billingSameAsShippingAddress) return true
    }
    const cu = this.customerService.customer()
    if (cu) {
      return this.validateAddressParts(o.addressBilling)
    }
    const valid = ! this.formErrorReceiver()["billing"]
    return valid
  }

  private validateAddressParts(t: PostalAddressInterface | undefined): boolean {
    if (t == undefined) return false
    if (! t.postalcode || ! t.postalcode.length) return false
    if (! t.city || ! t.city.length) return false
    if (! t.country) return false
    if (! t.firstname || ! t.firstname.length) return false
    if (! t.lastname || ! t.lastname.length) return false

    return true
  }


  validateShippingMethod(): boolean {
    return !! this.orderSignal().shippingMethod
  }

  validateAmounts(): boolean {
    const o = this.orderSignal()
    return o.amountInvoice !== undefined &&
      o.amountItems !== undefined && o.amountItems > 0 &&
      o.amountTax !== undefined &&
      o.amountShipping !== undefined
  }

  validateEmail(): boolean {
    const email = this.customerService.customer()?.email || this.order().email
    return !! email && EmailValidator.regex.test(email)
  }

  prepareOrderForLocalstorage(order: OrderInterface): OrderJsonInterface {
    let anyInterface = JSON.parse(JSON.stringify(order))
    delete anyInterface.billingSameAsShippingAddress
    delete anyInterface.taxesByRate
    return anyInterface as OrderJsonInterface
  }

  prepareOrderForSubmit(): OrderSubmitInterface {
    // calculate amounts
    this.onAfterChange()
    // remove references and remove/set valid properties
    let anyInterface = JSON.parse(JSON.stringify(this.order()))
    delete anyInterface.isDirty
    delete anyInterface.validationMessages

    delete anyInterface.taxesByRate
    delete anyInterface.email

    const status = this.status()

    if (! status.needed.addressShipping) {
      delete anyInterface.addressShipping
    } else {
      if (anyInterface.billingSameAsShippingAddress) {
        delete anyInterface.addressBilling
      }
    }

    delete anyInterface.billingSameAsShippingAddress

    let coupons = (anyInterface.coupons ?? []) as CouponInterface[]
    let submitCoupons: CouponJsonInterface[] = []
    let submitOrder = anyInterface as OrderSubmitInterface

    // ShippingMethodInterface -> ShippingMethodJsonInterface
    if (anyInterface.shippingMethod) {
      delete anyInterface.shippingMethod.isDirty
      submitOrder.shippingMethod = anyInterface.shippingMethod as ShippingMethodJsonInterface
    }

    // this comes from prepareCouponsForSubmit
    if (coupons.length) {

      let totalRedeemed: number = 0
      let totalBilling: number = submitOrder.amountInvoice

      for (let coupon of coupons) {
        // apply to CouponJsonInterface
        delete coupon.isDirty

        if (coupon.type == "PERCENT") {
          submitCoupons.push(coupon as CouponJsonInterface)
          continue
        }

        if (totalRedeemed + coupon.value <= totalBilling) {
          coupon.redeemed = coupon.value
        } else {
          coupon.redeemed = totalBilling - totalRedeemed
        }

        totalRedeemed += coupon.redeemed

        submitCoupons.push(coupon as CouponJsonInterface)
      }
    }
    // CouponInterface[] -> CouponJsonInterface[]
    submitOrder.coupons = submitCoupons

    // CartInterface -> CartSubmitInterface
    submitOrder.cart = this.cartService.getSubmitInterface()

    let customer = this.customerService.customer()
    // let customerJson: CustomerResponseInterface | NewCustomerResponseInterface

    // Customer -> CustomerResponseInterface
    if (customer) {
      submitOrder.customer = JSON.parse(JSON.stringify(customer))
      // submitOrder.user = this.authService.user()
    } else {
      submitOrder.customer = this.prepareNewCustomer()
    }

    return submitOrder
  }

  /**
   * if there is no user logged in, this prepares the order.user property for
   * submission.
   *
   * @private
   */
  private prepareNewCustomer(): NewCustomerResponseInterface {
    const names = this.getNamesFromAddresses([this.order().addressBilling, this.order().addressShipping])

    return {
      id: 0,
      email: this.order().email || '',
      orderPreferences: {
        defaultAddressBillingId: null,
        defaultAddressShippingId: null,
        defaultBillingMethod: null,
        defaultDeliveryMethod: null,
        defaultPayment: null,
        userId: null
      },
      addresses: [],
      preferences: [],
      salutation: "",
      title: "",
      firstname: names.length > 0 ? names[0] : "",
      lastname: names.length > 1 ? names[1] : "",
    }
  }

  getNamesFromAddresses(ads: (PostalAddressInterface | undefined)[]): string[] {
    for (let ad of ads) {
      if (! ad) continue
      if (ad.firstname && ad.lastname && ad.firstname.length > 0
        && ad.lastname.length > 0) {
        return [ ad.firstname, ad.lastname ]
      }
    }

    return ['' , '']
  }

  getCustomerEmail(): string {
    return (this.customerService.customer()?.email || this.order().email) + ''
  }

  getCustomerName(): string {
    const c = this.customerService.customer()
    if (c) {
      return c.firstname + ' ' + c.lastname
    }
    const names = this.getNamesFromAddresses([this.order().addressShipping, this.order().addressBilling])
    return names[0] + ' ' + names[1]
  }

  public submit(): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      this.pageLoadService.getStatusSubject().next(true)
      this.httpService.create(this.prepareOrderForSubmit()).subscribe(response => {
        this.setOrderResponse(response)
        this.pageLoadService.getStatusSubject().next(false)
        subscriber.next(true)
      }, error => {
        // 710 means the order (items, prices, coupons, ...) have been corrected by the api
        if (error.status == 710) {
          this.parseAndReplaceDirtyOrder(error.error)
        }
        this.pageLoadService.getStatusSubject().next(false)
        subscriber.next(false)
      })
    })
  }

  /**
   * This tests if addresses are equal. If the checkId parameter is set to true, this doesn't
   * check if values are equal when ids are equal
   *
   * @param a
   * @param b
   * @param checkId
   *
   * @private
   */
  private isAddressEqual(a?: PostalAddressInterface, b?: PostalAddressInterface, checkId = false): boolean {
    if (! a || ! b) return false

    // check id if given
    if (checkId) {
      if (a.id > 0 && b.id > 0) {
        return a.id === b.id
      }
    }

    if (a.city != b.city) return false
    if (a.firstname != b.firstname) return false
    if (a.postalcode != b.postalcode) return false
    if (a.lastname != b.lastname) return false
    if (a.houseNumber != b.houseNumber) return false
    if (a.country?.code != b.country?.code) return false
    if (a.telephone != b.telephone) return false

    return true
  }

  /**
   * This is used only for http validation (cart changes and discount) and creates
   * the OrderInterface from the OrderResponseInterface.
   *
   * The cart will also be updated here with the potentially corrected items.
   *
   * The HTTP-Validation-Message will be set here
   *
   * @param order
   * @private
   */
  private parseAndReplaceDirtyOrder(order: OrderResponseInterface) {

    this.statusSignal.update((s) => {
      s.httpInvalid = (order.validationMessages && order.validationMessages.length) ? order.validationMessages : false
      if (s.httpInvalid) s.invalid = true
      return { ... s }
    })

    const replaceWithOrder: OrderInterface = {
      ... this.getFromResponse(order),
      ... this.calculateTaxAmounts(order),
    }

    let c = CartService.getFromResponse(order.cart)
    if (order.addressShipping?.country) {
      this.cartService.setTaxCountry(order.addressShipping?.country)
    }

    this.cartService.replace(c)
    this._order = replaceWithOrder
    this.onChange()
  }

  private persist(order = this._order) {
    this.storage.set('order', this.prepareOrderForLocalstorage(order), 1209600) // 14 days
    this.orderSignal.set({ ... order })
  }

  public clear() {
    this.customerId = 0
    this._order = this.getNewOrder()
    this.cartService.clear()
    this.orderSignal.set(this._order)
    this.statusSignal.set(this.getInitialStatus())
    this.onChange()
  }

  public static getOrderProcessForOrderNumber(order: OrderPersistedInterface, orderNumber?: string) : OrderProcessInterface {
    // unknown is for old orders where no information exists, store pickup was not possible, so
    // use the OnlineOrderProcessInterface
    const unknown: OnlineOrderProcessInterface = {
      status: {
        code: "Neu",
        label: "Neu"
      },
      orderNumber: orderNumber ? orderNumber : "unbekannt",
      pickupQrCode: null,
      pickupStatus: null
    }

    if (! order.orderStatus) return unknown
    if (! orderNumber) return unknown

    const statuss = order.orderStatus.filter(r => { return r.orderNumber === orderNumber })

    if (statuss.length == 1) {
      if (statuss[0].pickupStatus) {
        const items = order.cart.items.filter(i => { return i.orderNumber == orderNumber })
        statuss[0].store = items.length ? items[0].store : undefined
      }
      return statuss[0]
    }

    return unknown
  }

  public static getTrackingLinksforOrderNumber(links: TrackingLinkInterface[], orderNumber: string) : TrackingLinkInterface[] {
    return links.filter(r => { return r.orderNumber === orderNumber })
  }

  public validatePaypalAuthorizationData(ad: IClientAuthorizeCallbackData): Observable<undefined> {
    if (this.orderResponse?.cart?.uuid) return this.httpService.validatePaypalAuthorizationData(this.orderResponse!.cart!.uuid, ad)
    throw Error("Could not find Order Response which must exist before validating Auth Data")
  }

  public cancelPaypalOrder(): Observable<undefined> {
    if (this.orderResponse?.cart?.uuid) return this.httpService.cancelPaypalOrder(this.orderResponse!.cart!.uuid)
    throw Error("Could not find Order Response which must exist")
  }

  public findRefundableOrder(request: RefundRequestInterface): Observable<RefundableOrderViewInterface> {
    return this.httpService.findRefundableOrder(request).pipe(
      map((response): RefundableOrderViewInterface => ({
        order: this.getFromResponse(response),
        uuid: response.uuid,
      }))
    )
  }

  public submitRefund(data: RefundSubmitInterface) {
    return this.httpService.submitRefund(data)
  }
}
